import { supabase } from './supabase';
import { formatPhoneDisplay } from './phone';
import { formatPropertyPrice, getDisplayCurrency } from './currency';
import type { ShowcaseData, CreateShowcaseResult, ShowcasePropertySummary } from '../types/types';

const OPERATION_LABELS: Record<string, string> = {
  sale: 'Venta',
  rent: 'Renta',
  sale_rent: 'Venta/Renta',
  vacational: 'Vacacional',
};

// Price in the currency the sharer had selected when the vitrina was minted
// (display_currency + the snapshotted rate travel with each summary row).
function formatPriceCompact(p: ShowcasePropertySummary): string {
  if (!p.price) return '';
  const formatted = formatPropertyPrice(p, p.display_currency ?? 'USD', p.exchange_rate_gtq ?? null);
  return p.price_label ? `${formatted} ${p.price_label}` : formatted;
}

function formatLocation(p: ShowcasePropertySummary): string {
  const parts = [p.sector_name, p.city_name, p.department_name].filter(Boolean);
  return parts.join(', ');
}

function formatTypeOperation(p: ShowcasePropertySummary): string {
  return [p.property_type_name, OPERATION_LABELS[p.operation]].filter(Boolean).join(' en ');
}

/**
 * Create a property showcase (vitrina) for WhatsApp sharing.
 */
export async function createShowcase(
  propertyIds: string[],
  hideContact: boolean = false
): Promise<CreateShowcaseResult> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase.rpc('create_showcase', {
    p_property_ids: propertyIds,
    p_hide_contact: hideContact,
    p_display_currency: getDisplayCurrency(),
  });

  if (error) throw error;
  return data as CreateShowcaseResult;
}

/**
 * Fetch a showcase by its token (UUID) for the public vitrina page.
 */
export async function fetchShowcase(showcaseId: string): Promise<ShowcaseData | null> {
  if (!supabase) throw new Error('Supabase not initialized');

  const { data, error } = await supabase.rpc('fetch_showcase', {
    p_showcase_id: showcaseId,
  });

  if (error) throw error;
  if (!data || data.error === 'not_found') return null;
  return data as ShowcaseData;
}

/**
 * Build the vitrina share URL for WhatsApp messages.
 * Routed via the `vitrina-shortener` Cloudflare Worker, which proxies to the
 * Supabase edge function that serves OG meta tags for link previews and
 * auto-redirects humans to the SPA.
 */
const VITRINA_SHARE_BASE =
  import.meta.env.VITE_VITRINA_SHARE_BASE || 'https://propiedades.intercambioinmobiliario.com';

export function buildVitrinaUrl(shortCode: string): string {
  return `${VITRINA_SHARE_BASE}/${shortCode}`;
}

/**
 * Append a "Name + phone" sign-off block when the sender has a phone.
 * WhatsApp auto-detects "+digits" and makes it tappable. Returns [] if no
 * phone is on file, in which case we skip the block entirely (the header
 * already attributes the share to the sender by name).
 */
function buildSenderTrailer(agentName: string, senderPhone?: string): string[] {
  const phone = formatPhoneDisplay(senderPhone);
  if (!phone) return [];
  // WhatsApp renders *_text_* as bold + italic. Phone stays plain so the
  // auto-link detector turns it into a tappable chat.
  // NO icon before the phone: emoji here have failed twice — astral-plane
  // (📲) decoded as U+FFFD "�" out of the whatsapp:// URL scheme, and even
  // BMP ☎ U+260E rendered as "?" on some clients. Plain text only.
  return ['', `*_${agentName}_*`, phone];
}

/**
 * Build the WhatsApp message text based on single or multiple properties.
 *
 * Single: "*Title*\n\n- details...\n\nURL" (sender shown via trailer block)
 * Multiple: "Revisa estas N propiedades enviadas por Agency/Agent\n\nURL"
 *
 * When `hideContact` is true the multi-property "enviadas por …" attribution
 * and the trailing name+phone sign-off are both omitted so the sender stays
 * anonymous, matching the vitrina page itself.
 */
export function buildWhatsAppMessage(
  properties: ShowcasePropertySummary[],
  agentName: string,
  agencyName: string,
  vitrinaUrl: string,
  hideContact: boolean = false,
  senderPhone?: string
): string {
  if (properties.length === 1) {
    const p = properties[0];
    const typeOp = formatTypeOperation(p);
    const location = formatLocation(p);
    const price = formatPriceCompact(p);

    const lines: string[] = [`*${p.title}*`, ''];
    if (typeOp) lines.push(`- ${typeOp}`);
    if (price) lines.push(`- ${price}`);
    if (location) lines.push(`- ${location}`);
    // "habitación" loses its accent in the plural — "habitaciónes" is misspelled.
    if (p.bedrooms) lines.push(`- ${p.bedrooms}${p.bedrooms_plus ? '+' : ''} ${p.bedrooms > 1 ? 'habitaciones' : 'habitación'}`);
    if (p.bathrooms) lines.push(`- ${p.bathrooms}${p.bathrooms_plus ? '+' : ''} baño${p.bathrooms > 1 ? 's' : ''}`);
    if (p.parking) lines.push(`- ${p.parking}${p.parking_plus ? '+' : ''} parqueo${p.parking > 1 ? 's' : ''}`);
    if (p.built_up_area) lines.push(`- Construcción: ${p.built_up_area} m²`);
    if (p.lot_size_varas) lines.push(`- Terreno: ${p.lot_size_varas} v²`);
    lines.push('');
    lines.push(vitrinaUrl);
    if (!hideContact) lines.push(...buildSenderTrailer(agentName, senderPhone));

    return lines.join('\n');
  }

  // Multiple properties
  const header = hideContact
    ? `Revisa estas ${properties.length} propiedades`
    : `Revisa estas ${properties.length} propiedades enviadas por ${agencyName}`;
  const lines = [header, '', vitrinaUrl];
  if (!hideContact) lines.push(...buildSenderTrailer(agentName, senderPhone));
  return lines.join('\n');
}

function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Open WhatsApp with a pre-filled message.
 *
 * Desktop: navigate a new tab to wa.me — opens WhatsApp Web with the message
 * pre-filled in the contact picker.
 *
 * Mobile: navigate the current tab to the `whatsapp://` URL scheme. wa.me's
 * no-recipient share picker is unreliable on iOS (it opens the app but drops
 * the pre-filled text on the contact picker), while the app scheme reliably
 * carries the text into the picker. After 1.5s, if the page is still visible
 * (e.g. WhatsApp not installed), we fall back to wa.me so the user still
 * reaches WhatsApp Web in the browser with the message attached.
 *
 * iOS Safari note: `window.open(_, '_blank')` requires a live user-gesture
 * context, which `await` destroys. Callers that do async work before they
 * have the message should call `prepareWhatsAppWindow()` synchronously inside
 * the click handler and pass the returned reference here.
 */
export function openWhatsApp(message: string, preOpenedWindow?: Window | null): void {
  const text = encodeURIComponent(message);

  if (isMobileDevice()) {
    // The pre-opened window from prepareWhatsAppWindow is for desktop only;
    // close it before navigating so we don't leave a stray about:blank tab.
    if (preOpenedWindow && !preOpenedWindow.closed) preOpenedWindow.close();
    const appUrl = `whatsapp://send?text=${text}`;
    const webUrl = `https://wa.me/?text=${text}`;
    const fallbackTimer = window.setTimeout(() => {
      // If we're still here, WhatsApp didn't take over — go to web fallback.
      window.location.href = webUrl;
    }, 1500);
    const onVisibilityChange = () => {
      if (document.hidden) {
        window.clearTimeout(fallbackTimer);
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.location.href = appUrl;
    return;
  }

  const url = `https://wa.me/?text=${text}`;
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.location.href = url;
    return;
  }
  const w = window.open(url, '_blank');
  if (!w) {
    // Popup blocked — fall back to same-tab navigation so the user still lands in WhatsApp.
    window.location.href = url;
  }
}

/**
 * Synchronously open a placeholder window during a click handler so it survives
 * the async work that follows. Pass the returned reference to `openWhatsApp`.
 *
 * Returns null on mobile because we route mobile through a same-tab `whatsapp://`
 * navigation instead of a new browser tab.
 */
export function prepareWhatsAppWindow(): Window | null {
  if (isMobileDevice()) return null;
  return window.open('about:blank', '_blank');
}

// Strip WhatsApp markdown (*bold*, _italic_) so plain-text email bodies don't
// render asterisks/underscores literally.
function stripWhatsAppMarkdown(text: string): string {
  return text.replace(/\*([^*\n]+)\*/g, '$1').replace(/_([^_\n]+)_/g, '$1');
}

export function buildEmailParts(
  properties: ShowcasePropertySummary[],
  agentName: string,
  agencyName: string,
  vitrinaUrl: string,
  hideContact: boolean,
  senderPhone?: string,
): { subject: string; body: string } {
  const subject = properties.length === 1
    ? `Te comparto una propiedad: ${properties[0].title}`
    : `Te comparto ${properties.length} propiedades`;
  const body = stripWhatsAppMarkdown(
    buildWhatsAppMessage(properties, agentName, agencyName, vitrinaUrl, hideContact, senderPhone),
  );
  return { subject, body };
}

/**
 * Open the user's default mail client with a pre-filled subject + body. Most
 * clients cap mailto: URLs around ~2000 chars; multi-property shares stay
 * well under that since the vitrina URL carries the heavy lifting.
 */
export function openEmail(subject: string, body: string): void {
  const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = url;
}

/**
 * Open the native share sheet via the Web Share API. Available on iOS Safari,
 * Android Chrome, recent desktop Chrome/Edge/Safari. Returns true if the share
 * was initiated, false if the API isn't available (caller can fall back).
 */
export async function openSystemShare(title: string, text: string, url: string): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.share) return false;
  try {
    await navigator.share({ title, text, url });
    return true;
  } catch (err: any) {
    // User canceled the share sheet — treat as success so we don't double-fall-back.
    if (err?.name === 'AbortError') return true;
    return false;
  }
}

export async function copyVitrinaUrl(vitrinaUrl: string): Promise<void> {
  await navigator.clipboard.writeText(vitrinaUrl);
}

/**
 * Copy text that only becomes available after an async step (e.g. minting a
 * vitrina short_code over the network) WITHOUT losing the clipboard on Safari.
 *
 * iOS/desktop Safari invalidate the tap's transient user-activation the instant
 * you `await` a network call, so a later `navigator.clipboard.writeText()`
 * throws `NotAllowedError` ("The request is not allowed by the user agent or the
 * platform in the current context…"). Safari (and modern Chromium) instead let
 * you hand the clipboard a `Promise<Blob>` via `ClipboardItem` synchronously
 * inside the gesture — the browser keeps the activation alive until it resolves.
 *
 * CRITICAL: call this synchronously from the click/tap handler, BEFORE any other
 * `await`, and pass the in-flight promise (don't await it first) — otherwise the
 * gesture is already gone and Safari rejects the write again.
 *
 * Falls back to `writeText` for engines without async `ClipboardItem` (older
 * Firefox), which are lenient enough about activation for the plain path.
 */
export async function copyTextWhenReady(textPromise: Promise<string>): Promise<void> {
  const canAsyncWrite =
    typeof ClipboardItem !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard?.write;
  if (canAsyncWrite) {
    try {
      // `navigator.clipboard.write(...)` is invoked synchronously here (the
      // arg is evaluated before `await` suspends), so the gesture is preserved.
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': textPromise.then((t) => new Blob([t], { type: 'text/plain' })),
        }),
      ]);
      return;
    } catch {
      // Engine exposes ClipboardItem but rejects promise-typed entries — fall
      // back to the resolved-value path below.
    }
  }
  const text = await textPromise;
  await navigator.clipboard.writeText(text);
}

export function isWebShareSupported(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}
